import _ from 'lodash';
import memoize from 'lru-memoize';
import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {buildTeammateDisplayName, doesTeammateMatchQuery} from '../../../../helpers/modelHelpers';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {DropdownItemIcon} from '../dropdownItemIcon';
import {DropdownItemTeammateAvatar} from '../dropdownItemTeammateAvatar';
import {TypedRowValue} from '../source/dropdownSource';
import {SyncDropdownSource} from '../source/syncDropdownSource';
import {DeprecatedSearchDropdown} from './deprecatedSearchDropdown';

/*
 * Constants.
 */

enum SearchTeammateDropdownItemsEnum {
  TEAMMATE = 'teammate',
  NONE = 'none',
}

/*
 * Props.
 */

interface SearchTeammateDropdownProps extends DropdownProps {
  teammates: ReadonlyArray<TeammateModel>;
  selectedEmail: string | undefined;
  shouldShowDeleteOption?: boolean;
  onChange: HandlerOf<TeammateModel>;
  onDelete?: Handler;
}

type SearchTeammateDropdownRowTypedValue =
  | TypedRowValue<SearchTeammateDropdownItemsEnum.TEAMMATE, TeammateModel>
  | TypedRowValue<SearchTeammateDropdownItemsEnum.NONE, undefined>;

/*
 * Components.
 */

export const SearchTeammateDropdown: FC<SearchTeammateDropdownProps> = (props) => {
  const {teammates, selectedEmail, shouldShowDeleteOption, onChange, onDelete, ...dropdownProps} = props;

  const dropdownSource = new SyncDropdownSource<SearchTeammateDropdownRowTypedValue>({
    items: buildDropdownItems(teammates, shouldShowDeleteOption),
    rowFilter: filterRows,
    renderRow: (row) => {
      if (row.type === SearchTeammateDropdownItemsEnum.NONE) {
        return renderNoneOption(!selectedEmail, onDelete);
      }
      return renderTeammate(row.value, selectedEmail, onChange);
    },
  });

  return (
    <DeprecatedSearchDropdown
      {...dropdownProps}
      title={<Trans id="customerview-search-teammate-title">Teammate</Trans>}
      source={dropdownSource}
    />
  );
};

/*
 * Helpers.
 */

function buildDropdownItems(
  teammates: ReadonlyArray<TeammateModel>,
  shouldShowDeleteOption: boolean | undefined,
): ReadonlyArray<SearchTeammateDropdownRowTypedValue> {
  return _.compact([
    shouldShowDeleteOption ? buildDropdownNoneOption() : undefined,
    ...teammates.map(buildDropdownTeammate),
  ]);
}

function buildDropdownTeammate(value: TeammateModel): SearchTeammateDropdownRowTypedValue {
  return {
    type: SearchTeammateDropdownItemsEnum.TEAMMATE,
    value,
  };
}

function buildDropdownNoneOption(): SearchTeammateDropdownRowTypedValue {
  return {
    type: SearchTeammateDropdownItemsEnum.NONE,
    value: undefined,
  };
}

function renderTeammate(
  teammate: TeammateModel,
  selectedEmail: string | undefined,
  onSelect: HandlerOf<TeammateModel>,
) {
  return (
    <DropdownItem isChecked={teammate.email === selectedEmail} onSelect={() => onSelect(teammate)}>
      <DropdownItemTeammateAvatar teammate={teammate} />
      <DropdownItemContent>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
    </DropdownItem>
  );
}

function renderNoneOption(isChecked: boolean, onSelect: Handler | undefined) {
  return (
    <DropdownItem
      onSelect={() => {
        if (onSelect) {
          onSelect();
        }
      }}
      isChecked={isChecked}
    >
      <DropdownItemIcon iconName="none" label={null} />
      <DropdownItemContent>
        <Trans id="search-teammate-dropdown-none">None</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

const filterRows = memoize(20)((rows: ReadonlyArray<SearchTeammateDropdownRowTypedValue>, query: string) => {
  if (!query) {
    return rows;
  }

  return rows.filter(
    (t) => t.type === SearchTeammateDropdownItemsEnum.TEAMMATE && doesTeammateMatchQuery(t.value, query),
  );
});
